/**
 * GPS Utilities
 * Standalone GPS functions that can be called from anywhere (hooks, utils, etc.)
 * Extracted from useGPS hook for non-component contexts
 */

/**
 * Convert decimal degrees to degrees/minutes format
 */
const formatCoordinate = (decimal, isLat) => {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutes = ((absolute - degrees) * 60).toFixed(3);
  const direction = isLat
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W');
  return `${degrees}°${minutes}'${direction}`;
};

/**
 * Get current GPS position
 * @returns {Promise<{ lat: number, lng: number, accuracy: number, timestamp: string, formatted: { lat: string, lng: string }}>}
 */
export const getGPSPosition = () => {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Math.round(pos.coords.accuracy),
          timestamp: new Date().toISOString(),
          formatted: {
            lat: formatCoordinate(pos.coords.latitude, true),
            lng: formatCoordinate(pos.coords.longitude, false)
          }
        };
        resolve(coords);
      },
      (err) => {
        let errorMsg = 'Unable to get position';
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMsg = 'Location permission denied';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMsg = 'Location unavailable';
            break;
          case err.TIMEOUT:
            errorMsg = 'Location request timed out';
            break;
        }
        reject(errorMsg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

export default { getGPSPosition };
